import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, ArrowRight, CalendarDays } from 'lucide-react';
import { Button } from './ui/button';

/**
 * WebinarCTA — compact educational CTA strip used throughout the site.
 *
 * Positioned between page sections to surface the webinar series
 * contextually, tying the page subject to "come see it live".
 *
 * Props:
 *   headline  – page-specific hook line
 *   body      – one-sentence contextual reason to join
 *   cta       – button label (default "Browse upcoming webinars")
 */
const WebinarCTA = ({
  headline = 'See it in action.',
  body = 'Our free webinar series is designed for SAP Business One partners. No sales pitches: just the platform, demonstrated live.',
  cta = 'Browse upcoming webinars',
}) => {
  const navigate = useNavigate();

  return (
    <section className="relative bg-gradient-to-r from-blue-50 via-white to-blue-50 border-y border-blue-100 overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(59 130 246) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      </div>

      <div className="container mx-auto px-4 lg:px-8 py-10 relative z-10">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 max-w-5xl mx-auto">

          {/* Icon + text */}
          <div className="flex items-start gap-5">
            {/* Icon box with live pulse */}
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-600 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Video className="w-7 h-7 text-white" />
              </div>
              {/* Pulsing "live / upcoming" dot */}
              <span className="absolute -top-1 -right-1 flex h-4 w-4">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500 border-2 border-white" />
              </span>
            </div>

            {/* Copy */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold text-cyan-600 uppercase tracking-widest">Free Partner Webinars</span>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1 leading-snug">{headline}</h3>
              <p className="text-slate-500 text-sm leading-relaxed max-w-xl">{body}</p>
            </div>
          </div>

          {/* CTA */}
          <Button
            className="bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-500 hover:to-blue-600 text-white font-bold px-6 py-3 flex-shrink-0 shadow-md shadow-blue-500/20"
            onClick={() => navigate('/webinars')}
            data-testid="webinar-cta-btn"
          >
            {cta}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default WebinarCTA;
